export function getPassiveLevel(pct) {
  if (pct == null || isNaN(Number(pct))) return "N/A";
  const p = Number(pct);
  if (p <= 10) return "Muy bueno";
  if (p <= 20) return "Normal/controlado";
  if (p <= 35) return "Revisable";
  if (p <= 50) return "Alto";
  return "Crítico";
}

export const isValidNumber = (value) => Number.isFinite(Number(value));

export const toNumberOrNull = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const getPassiveValue = (point) => {
  if (!point || typeof point !== "object") return null;

  const passiveEnergy = toNumberOrNull(point.passive_energy);
  if (passiveEnergy !== null) return passiveEnergy;

  const totalEnergy = toNumberOrNull(point.total_energy);
  if (totalEnergy !== null) return totalEnergy;

  const energy = toNumberOrNull(point.energy);
  if (energy !== null) return energy;

  return null;
};

const buildPointRecord = (point) => {
  const value = getPassiveValue(point);
  if (value === null) return null;
  return {
    timestamp: point.timestamp,
    value,
    originalPoint: point,
  };
};

export const getPassivePoints = (series) => {
  if (!Array.isArray(series)) return [];

  const passivePoints = series.reduce((result, point) => {
    if (point?.is_passive !== true) return result;
    const record = buildPointRecord(point);
    if (!record) return result;
    result.push(record);
    return result;
  }, []);

  if (passivePoints.length > 0) return passivePoints;

  return getAnalysisPoints(series);
};

export const getAnalysisPoints = (series) => {
  if (!Array.isArray(series)) return [];

  const collect = (filter) =>
    series.reduce((result, point) => {
      if (!filter(point)) return result;
      const record = buildPointRecord(point);
      if (!record) return result;
      result.push(record);
      return result;
    }, []);

  const passivePoints = collect((point) => point?.is_passive === true);
  if (passivePoints.length > 0) return passivePoints;

  const nonProductivePoints = collect((point) => point?.is_non_productive === true);
  if (nonProductivePoints.length > 0) return nonProductivePoints;

  const noProductivoPoints = collect((point) =>
    String(point?.period_type).toUpperCase() === "NO_PRODUCTIVO"
  );
  return noProductivoPoints;
};

export const percentile = (values, p) => {
  if (!Array.isArray(values) || values.length === 0) return null;
  const numericValues = values
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));
  if (numericValues.length === 0) return null;

  const sorted = numericValues.slice().sort((a, b) => a - b);
  const rank = (Number(p) / 100) * (sorted.length - 1);
  const lowerIndex = Math.floor(rank);
  const upperIndex = Math.ceil(rank);
  const fraction = rank - lowerIndex;

  if (upperIndex >= sorted.length) {
    return sorted[sorted.length - 1];
  }

  if (lowerIndex === upperIndex) {
    return sorted[lowerIndex];
  }

  return sorted[lowerIndex] + (sorted[upperIndex] - sorted[lowerIndex]) * fraction;
};

export const calculateZScore = (value, mean, stdDev) => {
  const numericValue = toNumberOrNull(value);
  const numericMean = toNumberOrNull(mean);
  const numericStd = toNumberOrNull(stdDev);
  if (numericValue === null || numericMean === null || !Number.isFinite(numericStd) || numericStd === 0) {
    return 0;
  }
  return (numericValue - numericMean) / numericStd;
};

export const formatPassiveTimestamp = (value) => {
  if (value == null || value === "") return "-";

  const date = typeof value === "string" || typeof value === "number"
    ? new Date(value)
    : value instanceof Date
    ? value
    : null;

  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return String(value);
  }

  const pad = (num) => String(num).padStart(2, "0");
  const day = pad(date.getUTCDate());
  const month = pad(date.getUTCMonth() + 1);
  const year = date.getUTCFullYear();
  const hours = pad(date.getUTCHours());
  const minutes = pad(date.getUTCMinutes());

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

export const translateAnomalyOrigin = (value) => {
  if (value == null || value === "") return "-";
  const normalized = String(value).toLowerCase();
  const map = {
    statistical: "Estadístico",
    predictive: "Predictivo",
    combined: "Combinado",
  };
  return map[normalized] ?? String(value);
};

export const calculatePassiveStatistics = (series) => {
  const points = getPassivePoints(series);
  if (points.length < 2) return null;

  const values = points.map((point) => point.value);
  const count = values.length;
  const mean = values.reduce((sum, value) => sum + value, 0) / count;
  const median = percentile(values, 50);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const p25 = percentile(values, 25);
  const p75 = percentile(values, 75);
  const p90 = percentile(values, 90);

  const variance = values.reduce((acc, value) => acc + Math.pow(value - mean, 2), 0) / (count - 1);
  const stdDev = Math.sqrt(variance);

  const maxZScore = values.reduce((maxValue, value) => {
    const z = calculateZScore(value, mean, stdDev);
    return Math.max(maxValue, Math.abs(z));
  }, 0);

  const thresholdExcess = mean + 2 * stdDev;

  return {
    count,
    mean,
    median,
    stdDev,
    min,
    max,
    p25,
    p75,
    p90,
    maxZScore,
    thresholdExcess,
  };
};

export const calculateExcessProbability = (series, statistics) => {
  if (!statistics || statistics.thresholdExcess == null) return null;
  const points = getPassivePoints(series);
  if (points.length === 0) return null;

  const threshold = toNumberOrNull(statistics.thresholdExcess);
  if (threshold === null) return null;

  const exceeding = points.filter((point) => point.value > threshold).length;
  return exceeding / points.length;
};

export const calculateSimplePassiveAnomalies = (series, statistics) => {
  const points = getPassivePoints(series);
  if (!statistics || points.length === 0) return [];

  const expectedValue = statistics.median;
  if (!Number.isFinite(expectedValue)) return [];

  const anomalies = points
    .map((point) => {
      const difference = point.value - expectedValue;
      const zScore = calculateZScore(point.value, statistics.mean, statistics.stdDev);
      const classification = zScore > 3
        ? "Crítico"
        : zScore > 2
        ? "Anómalo"
        : zScore > 1
        ? "Revisar"
        : null;

      if (!classification) return null;

      return {
        timestamp: point.timestamp,
        realValue: point.value,
        expectedValue,
        difference,
        zScore,
        classification,
        source: "statistical",
      };
    })
    .filter(Boolean);

  const priority = { Crítico: 0, Anómalo: 1, Revisar: 2 };

  return anomalies
    .sort((a, b) => {
      const groupDiff = priority[a.classification] - priority[b.classification];
      if (groupDiff !== 0) return groupDiff;
      return b.zScore - a.zScore;
    })
    .slice(0, 20);
};

export const buildSimplePredictiveSeries = (series, statistics) => {
  const points = getPassivePoints(series);
  if (!statistics || points.length === 0) return [];

  const expectedValue = statistics.median;
  if (!Number.isFinite(expectedValue)) return [];

  return points.map((point) => ({
    timestamp: point.timestamp,
    realValue: point.value,
    expectedValue,
    residual: point.value - expectedValue,
    zScore: calculateZScore(point.value, statistics.mean, statistics.stdDev),
  }));
};

export const calculateSimpleModelMetrics = (predictiveSeries) => {
  if (!Array.isArray(predictiveSeries) || predictiveSeries.length < 2) return null;

  const nRecords = predictiveSeries.length;
  const mae = predictiveSeries.reduce((sum, point) => sum + Math.abs(point.residual), 0) / nRecords;
  const rmse = Math.sqrt(
    predictiveSeries.reduce((sum, point) => sum + Math.pow(point.residual, 2), 0) / nRecords
  );

  return {
    mae,
    rmse,
    nRecords,
    description: "Modelo simple basado en la mediana del consumo pasivo",
  };
};

export const safeNum = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

export const formatNumber = (v, decimals = 2) => {
  if (v == null || isNaN(Number(v))) return "-";
  return Number(v).toFixed(decimals);
};

export const formatCurrency = (v) => {
  if (v == null || isNaN(Number(v))) return "$0.00";
  return `$${Number(v).toFixed(2)}`;
};

const passiveLoadHelpers = {
  getPassiveLevel,
  isValidNumber,
  toNumberOrNull,
  getPassiveValue,
  getPassivePoints,
  getAnalysisPoints,
  percentile,
  calculateZScore,
  calculatePassiveStatistics,
  calculateExcessProbability,
  calculateSimplePassiveAnomalies,
  buildSimplePredictiveSeries,
  calculateSimpleModelMetrics,
  formatPassiveTimestamp,
  translateAnomalyOrigin,
  safeNum,
  formatNumber,
  formatCurrency,
};

export default passiveLoadHelpers;
